import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components';
import { AuthLayout } from './AuthLayout';

/**
 * Requests a Supabase recovery email. `redirectTo` must be on the project's
 * allowed redirect list or Supabase silently sends the user to the Site URL
 * instead, which would skip the reset form entirely.
 */
export const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });

    setBusy(false);
    if (resetError) {
      setError(resetError.message);
      return;
    }
    // Deliberately the same outcome whether or not the address exists, so this
    // page cannot be used to enumerate who has an account.
    setSent(true);
  }

  const backToSignIn = <Link to="/auth/sign-in">Back to sign in</Link>;

  if (sent) {
    return (
      <AuthLayout
        eyebrow="Check your email"
        title="Reset link sent"
        lede="If an account exists for that address, a password reset link is on its way."
        footer={backToSignIn}
      >
        <div className="auth__notice">
          We sent a link to <strong>{email}</strong>. It expires in one hour. If it does not
          arrive, check spam, then try again.
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      eyebrow="Forgot password"
      title="Reset your password"
      lede="Enter the email address you sign in with and we'll send you a reset link."
      footer={backToSignIn}
    >
      <form className="auth__form" onSubmit={onSubmit}>
        <div className="auth__field">
          <label className="field-label" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            type="email"
            className="text-input"
            value={email}
            autoComplete="username"
            autoFocus
            required
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        {error && (
          <div className="auth__error" role="alert">
            {error}
          </div>
        )}

        <Button type="submit" variant="primary" fullWidth loading={busy}>
          Send reset link
        </Button>
      </form>
    </AuthLayout>
  );
};

export default ForgotPassword;
